import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable, lastValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { HelloWebService } from '../openapi/services/hello-web.service';

import { LoggerService } from './logger.service';

import { authentication, revokeAuthentication } from 'shared/auth/store/auth.actions';
import { IAuthStore } from 'shared/auth/store/auth.reducer';
import { ApplicationType } from 'shared/openapi/models/application-type';
import { ChannelTypeAbbreviation } from 'shared/openapi/models/channel-type-abbreviation';
import { CsrfTokenCookieName } from 'shared/openapi/models/csrf-token-cookie-name';
import { ExpirationCookieName } from 'shared/openapi/models/expiration-cookie-name';
import { LogoutService } from 'shared/openapi/services/logout.service';
import { RefreshSessionService } from 'shared/openapi/services/refresh-session.service';
import { AuthBaseService } from 'shared/services/auth-base.service';

export abstract class InternalAuthBaseService extends AuthBaseService {
	protected constructor(
		logger: LoggerService,
		dialog: MatDialog,
		router: Router,
		store: Store<{ auth: IAuthStore }>,
		protected helloService: HelloWebService,
		protected logoutService: LogoutService,
		protected refreshSessionService: RefreshSessionService,
		protected cookieService: CookieService
	) {
		super(logger, dialog, router, store);
	}

	public override logout() {
		this.logoutService.logout({ channelType: ChannelTypeAbbreviation.Web }).subscribe({
			next: () => {
				this.store.dispatch(revokeAuthentication());
				this.router.navigate(['login']);
			},
			error: (error) => {
				this.logger.log(this.constructor.name, this.logout.name, error);
				this.store.dispatch(revokeAuthentication());
				this.router.navigate(['login']);
			},
		});
	}

	public override keepSessionAlive(): void {
		this.refreshSessionService.keepAlive({ channelType: ChannelTypeAbbreviation.Web }).subscribe({
			next: () => {},
			error: (error) => this.logger.log(this.constructor.name, this.keepSessionAlive.name, error),
		});
	}

	protected override async handleSessionOnInit(expirationKey: string): Promise<void> {
		if (this.cookieService.check(expirationKey)) {
			try {
				await lastValueFrom(
					this.refreshSessionService.refreshSession({
						channelType: ChannelTypeAbbreviation.Web,
					})
				);
				this.store.dispatch(authentication());
				await this.preload();
			} catch (error) {
				this.store.dispatch(revokeAuthentication());
			}
		} else {
			this.store.dispatch(revokeAuthentication());
		}
	}

	protected override onNotAuthenticated(): void {
		this.store
			.select('auth')
			.pipe(take(1))
			.subscribe((authStore) => {
				if (authStore.unauthorized) {
					this.cookieService.delete(ExpirationCookieName[authStore.app as keyof typeof ExpirationCookieName]);
					this.cookieService.delete(CsrfTokenCookieName[authStore.app as keyof typeof CsrfTokenCookieName]);
				}
				this.invokeHelloIfUserIsNotLoggedIn(
					CsrfTokenCookieName[authStore.app as keyof typeof CsrfTokenCookieName],
					authStore.app as ApplicationType
				)?.subscribe();
			});
	}

	protected override getSessionExpirationTime(expirationKey: string): number {
		const cookieExpirationDateTime = this.cookieService.get(expirationKey).replace(/"/g, '');
		if (cookieExpirationDateTime) {
			return new Date(cookieExpirationDateTime).getTime();
		}
		return 0;
	}

	public invokeHelloIfUserIsNotLoggedIn(
		csrfTokenName: string,
		applicationType: ApplicationType
	): Observable<void> | undefined {
		if (!this.cookieService.check(csrfTokenName)) {
			return this.invokeHello(applicationType);
		}
		return;
	}

	private invokeHello(applicationType: ApplicationType): Observable<void> {
		return this.helloService
			.helloWeb({
				'X-APP-TYPE': applicationType,
			})
			.pipe(map((response: void) => response));
	}
}
